// src/components/layout/Footer.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  ArrowUp,
  Cookie,
  X,
  HeartHandshake,
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { API_URL } from "../../lib/utils";
import "./footer-premium.css";

const COOKIE_KEY = "glgif_cookie_consent_v1";
/**
 * consent object shape:
 * {
 *   choice: "accepted" | "rejected" | "custom",
 *   prefs: { necessary: true, analytics: boolean, marketing: boolean },
 *   updatedAt: "ISO string"
 * }
 */

// ✅ Donate popup keys
const DONATE_POPUP_KEY = "glgif_donate_popup_v1";

export const Footer = () => {
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Cookie banner state
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [cookiePrefs, setCookiePrefs] = useState({
    necessary: true, // always true
    analytics: false,
    marketing: false,
  });

  // ✅ Donate popup state
  const [showDonatePopup, setShowDonatePopup] = useState(false);

  const whatsappNumber = "2349050399235";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    "Hello GLGIF, I would like to connect."
  )}`;

  // Show cookie banner only if no prior consent stored
  useEffect(() => {
    try {
      const raw = localStorage.getItem(COOKIE_KEY);
      if (!raw) {
        setShowCookieBanner(true);
        return;
      }
      // If stored but malformed, show again
      JSON.parse(raw);
      setShowCookieBanner(false);
    } catch {
      setShowCookieBanner(true);
    }
  }, []);

  // ✅ Donate popup (5 seconds after first load)
  // - shows once per session/day-ish (using localStorage timestamp)
  // - never shows on /admin routes
  useEffect(() => {
    // prevent showing on admin pages
    if (location.pathname.startsWith("/admin")) return;

    const timer = setTimeout(() => {
      try {
        const raw = localStorage.getItem(DONATE_POPUP_KEY);
        const lastShown = raw ? Number(raw) : 0;
        const now = Date.now();

        // show once every 24 hours (change to 0 if you want every visit)
        const DAY = 24 * 60 * 60 * 1000;

        if (!lastShown || now - lastShown > DAY) {
          setShowDonatePopup(true);
          localStorage.setItem(DONATE_POPUP_KEY, String(now));
        }
      } catch {
        // storage blocked: still show once
        setShowDonatePopup(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Optional: auto-close customize when route changes
  useEffect(() => {
    setShowCustomize(false);
  }, [location.pathname]);

  // Lock scroll when donate popup open (and allow ESC)
  useEffect(() => {
    if (!showDonatePopup) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowDonatePopup(false);
    };
    document.addEventListener("keydown", onKeyDown);

    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = original;
    };
  }, [showDonatePopup]);

  const saveCookieConsent = (payload) => {
    try {
      localStorage.setItem(COOKIE_KEY, JSON.stringify(payload));
    } catch {
      // If storage fails, still hide banner to avoid trapping user
    }
    setShowCookieBanner(false);
    setShowCustomize(false);
  };

  const acceptAllCookies = () => {
    saveCookieConsent({
      choice: "accepted",
      prefs: { necessary: true, analytics: true, marketing: true },
      updatedAt: new Date().toISOString(),
    });
    toast.success("Cookie preferences saved.");
  };

  const rejectAllCookies = () => {
    saveCookieConsent({
      choice: "rejected",
      prefs: { necessary: true, analytics: false, marketing: false },
      updatedAt: new Date().toISOString(),
    });
    toast.success("Cookie preferences saved.");
  };

  const saveCustomCookies = () => {
    saveCookieConsent({
      choice: "custom",
      prefs: { ...cookiePrefs, necessary: true },
      updatedAt: new Date().toISOString(),
    });
    toast.success("Cookie preferences saved.");
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/newsletter?email=${encodeURIComponent(cleanEmail)}`,
        { method: "POST" }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.detail || "Subscription failed");
      }

      if (data.status === "success") {
        toast.success("Successfully subscribed to newsletter!");
        setEmail("");
      } else if (data.status === "exists") {
        toast.info("You are already subscribed!");
      } else {
        toast.success("Subscription received.");
        setEmail("");
      }
    } catch (error) {
      toast.error(error.message || "Failed to subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const quickLinks = useMemo(
    () => [
      { label: "Home", href: "/" },
      { label: "About Us", href: "/about/who-we-are" },
      { label: "Our Projects", href: "/projects" },
      { label: "Get Involved", href: "/get-involved" },
      { label: "Blog", href: "/blog" },
      { label: "Events", href: "/events" },
      { label: "Contact", href: "/contact" },
    ],
    []
  );

  const programs = useMemo(
    () => [
      { label: "Touch A Thousand Lives", href: "/projects" },
      { label: "Send a Child to School", href: "/projects" },
      { label: "Feed the Child", href: "/projects" },
      { label: "Tech a School", href: "/projects" },
      { label: "Climate Action", href: "/projects" },
    ],
    []
  );

  const socialLinks = useMemo(
    () => [
      { icon: Facebook, href: "https://www.facebook.com/glgifofficial", label: "Facebook" },
      { icon: Instagram, href: "https://www.instagram.com/glgifofficial", label: "Instagram" },
      { icon: Linkedin, href: "https://www.linkedin.com/company/goldenlife-global-impact-foundation-glgif/", label: "LinkedIn" },
      { icon: Youtube, href: "https://www.youtube.com/@glgifofficial", label: "YouTube" },
    ],
    []
  );

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <>
      <footer className="footer-premium" data-testid="footer">
        {/* Newsletter */}
        <div className="footer-newsletter-wrap">
          <div className="footer-container">
            <div className="footer-newsletter-card">
              <div className="footer-newsletter-copy">
                <p className="footer-kicker">Stay Connected</p>
                <h3 className="footer-newsletter-title">Get updates on campaigns and impact stories</h3>
                <p className="footer-newsletter-sub">
                  Be the first to hear what GLGIF is doing across communities.
                </p>
              </div>

              <form onSubmit={handleNewsletterSubmit} className="footer-newsletter-form">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="footer-input"
                  required
                />
                <Button type="submit" disabled={loading} className="footer-subscribe-btn">
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Subscribing..." : "Subscribe"}
                </Button>
              </form>
            </div>
          </div>
        </div>

        {/* Main footer body */}
        <div className="footer-container footer-main">
          <div className="footer-grid">
            {/* Brand */}
            <div>
              <Link to="/" className="footer-brand">
                <div className="footer-brand-logo-wrap">
                  <img src="/assets/logo.png" alt="GLGIF Logo" className="footer-brand-logo" />
                </div>
                <div>
                  <span className="footer-brand-name">GLGIF</span>
                  <p className="footer-brand-tagline">Saving Lives, Redirecting Destinies</p>
                </div>
              </Link>

              <p className="footer-brand-text">
                GoldenLife Global Impact Foundation is built for dignity, protection, and opportunity
                through humanitarian action and long-term community empowerment.
              </p>

              {/* Optional small cookie prefs link */}
              <button
                type="button"
                onClick={() => {
                  setShowCookieBanner(true);
                  setShowCustomize(true);
                }}
                className="mt-4 inline-flex items-center gap-2 text-sm text-amber-200/90 hover:text-amber-200"
                aria-label="Open cookie preferences"
              >
                <Cookie className="w-4 h-4" />
                Cookie Preferences
              </button>
            </div>

            {/* Quick links */}
            <div>
              <h4 className="footer-title">Quick Links</h4>
              <ul className="footer-list">
                {quickLinks.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="footer-link">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Programs */}
            <div>
              <h4 className="footer-title">Our Programs</h4>
              <ul className="footer-list">
                {programs.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="footer-link">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="footer-title">Contact Us</h4>
              <ul className="footer-contact-list">
                <li className="footer-contact-item">
                  <MapPin className="footer-contact-icon" />
                  <span>Uyo, Akwa Ibom State, Nigeria</span>
                </li>
                <li className="footer-contact-item">
                  <Phone className="footer-contact-icon" />
                  <span>+2349050399235 &nbsp;&nbsp; +2347066155981</span>
                </li>
                <li className="footer-contact-item">
                  <Mail className="footer-contact-icon" />
                  <span>info@glgif.org</span>
                </li>
              </ul>

              <div className="footer-socials">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-btn"
                    aria-label={social.label}
                    title={social.label}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}

                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-social-btn"
                  aria-label="Chat with GLGIF"
                  title="Chat with us"
                >
                  <MessageSquare className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="footer-bottom">
          <div className="footer-container footer-bottom-inner">
            <p>&copy; {new Date().getFullYear()} GoldenLife Global Impact Foundation. All rights reserved.</p>
            <div className="footer-bottom-links">
              <Link to="/privacy" className="footer-link footer-small-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="footer-link footer-small-link">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating chat button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Floating chat button"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 shadow-xl transition-all"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="hidden sm:inline font-medium">Chat</span>
      </a>

      {/* Back to top button */}
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Back to top"
        title="Back to top"
        className="fixed bottom-24 right-6 z-50 inline-flex items-center justify-center rounded-full bg-stone-900 hover:bg-stone-800 text-white w-12 h-12 shadow-xl transition-all"
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      {/* ✅ Donate Popup (5 seconds after load) */}
      {showDonatePopup && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Donate popup">
          {/* Backdrop */}
          <button
            type="button"
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowDonatePopup(false)}
            aria-label="Close donate popup"
          />

          {/* Modal */}
          <div className="relative z-10 h-full w-full p-4 sm:p-6 flex items-center justify-center">
            <div className="w-full max-w-4xl rounded-3xl overflow-hidden bg-white shadow-2xl border border-stone-200">
              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative">
                  <img
                    src="/assets/hero-donate.png"
                    alt="Support GLGIF"
                    className="w-full h-56 md:h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-900/60 via-black/30 to-black/30" />
                  <div className="absolute left-5 bottom-5 right-5">
                    <span className="inline-flex items-center rounded-full bg-white/15 border border-white/25 backdrop-blur-sm px-4 py-2 text-xs font-semibold text-white tracking-wide">
                      GLGIF • Donate Now
                    </span>
                    <p className="mt-3 text-white font-heading text-2xl md:text-3xl font-extrabold leading-tight">
                      Help us reach more lives
                    </p>
                    <p className="mt-2 text-white/90 text-sm md:text-base max-w-sm">
                      Your support fuels relief, education, protection, empowerment, technology inclusion, and climate action.
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-7">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold text-amber-700">Make an impact today</p>
                      <h3 className="font-heading text-xl sm:text-2xl font-extrabold text-stone-900 mt-1">
                        Donate to support our missions
                      </h3>
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowDonatePopup(false)}
                      className="w-10 h-10 rounded-full border border-stone-200 bg-white hover:bg-stone-50 flex items-center justify-center shrink-0"
                      aria-label="Close donate popup"
                      title="Close"
                    >
                      <X className="w-5 h-5 text-stone-700" />
                    </button>
                  </div>

                  <div className="mt-4 rounded-2xl border border-stone-200 bg-stone-50 p-4">
                    <ul className="space-y-2 text-sm text-stone-700">
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                          <HeartHandshake className="h-4 w-4" />
                        </span>
                        <span>Support urgent outreach and welfare response.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                          <HeartHandshake className="h-4 w-4" />
                        </span>
                        <span>Help keep children in school and safe.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-800">
                          <HeartHandshake className="h-4 w-4" />
                        </span>
                        <span>Strengthen livelihoods and community resilience.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Button
                      asChild
                      className="rounded-full bg-amber-600 hover:bg-amber-700 text-white px-6 py-6 text-base"
                    >
                      <Link to="/get-involved#donate" onClick={() => setShowDonatePopup(false)}>
                        <HeartHandshake className="w-5 h-5 mr-2" />
                        Donate Now
                      </Link>
                    </Button>

                    <Button
                      asChild
                      variant="outline"
                      className="rounded-full border-2 px-6 py-6 text-base"
                    >
                      <Link to="/get-involved" onClick={() => setShowDonatePopup(false)}>
                        Learn More
                      </Link>
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-full text-stone-700"
                      onClick={() => setShowDonatePopup(false)}
                    >
                      Not now
                    </Button>
                  </div>

                  <p className="mt-4 text-xs text-stone-500">
                    This message may appear occasionally to help support GLGIF campaigns.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:pb-6"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="mx-auto max-w-4xl rounded-2xl border border-stone-200 bg-white shadow-2xl">
            {/* Top row */}
            <div className="flex items-start justify-between gap-4 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
                  <Cookie className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-heading text-lg font-bold text-stone-900">
                    We use cookies
                  </p>
                  <p className="text-sm text-stone-600 leading-relaxed">
                    We use essential cookies to keep this site secure and working.
                    With your permission, we may also use analytics cookies to improve our impact stories and user experience.
                    Learn more in our{" "}
                    <Link to="/privacy" className="text-amber-700 font-medium hover:text-amber-800">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowCookieBanner(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-stone-100 text-stone-600"
                aria-label="Close cookie banner"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-6 pb-5 sm:pb-6">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={acceptAllCookies}
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Accept all
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={rejectAllCookies}
                  className="rounded-full border-2"
                >
                  Reject non-essential
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCustomize((v) => !v)}
                  className="rounded-full text-stone-700"
                >
                  {showCustomize ? "Hide" : "Customize"}
                </Button>
              </div>

              <p className="text-xs text-stone-500">
                You can update this anytime via “Cookie Preferences” in the footer.
              </p>
            </div>

            {/* Customize panel */}
            {showCustomize && (
              <div className="border-t border-stone-200 px-5 sm:px-6 py-5 sm:py-6 bg-stone-50 rounded-b-2xl">
                <div className="grid sm:grid-cols-3 gap-4">
                  {/* Necessary */}
                  <div className="rounded-xl border border-stone-200 bg-white p-4">
                    <p className="font-medium text-stone-900">Necessary</p>
                    <p className="text-xs text-stone-600 mt-1">
                      Required for security and basic site functions.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2">
                      <input type="checkbox" checked disabled />
                      <span className="text-sm text-stone-700">Always on</span>
                    </div>
                  </div>

                  {/* Analytics */}
                  <div className="rounded-xl border border-stone-200 bg-white p-4">
                    <p className="font-medium text-stone-900">Analytics</p>
                    <p className="text-xs text-stone-600 mt-1">
                      Helps us understand visits and improve content.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={cookiePrefs.analytics}
                        onChange={(e) =>
                          setCookiePrefs((p) => ({ ...p, analytics: e.target.checked }))
                        }
                      />
                      <span className="text-sm text-stone-700">Allow</span>
                    </div>
                  </div>

                  {/* Marketing */}
                  <div className="rounded-xl border border-stone-200 bg-white p-4">
                    <p className="font-medium text-stone-900">Marketing</p>
                    <p className="text-xs text-stone-600 mt-1">
                      Used to personalize outreach and campaigns.
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={cookiePrefs.marketing}
                        onChange={(e) =>
                          setCookiePrefs((p) => ({ ...p, marketing: e.target.checked }))
                        }
                      />
                      <span className="text-sm text-stone-700">Allow</span>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    onClick={saveCustomCookies}
                    className="rounded-full bg-stone-900 hover:bg-stone-800 text-white"
                  >
                    Save preferences
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowCustomize(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;